<template>
  <div class="vl-parent">
    <loading
      v-model:active="isLoading"
      :can-cancel="false"
      :height="34"
      :is-full-page="true"
      :opacity="0.3"
      :width="34"
      background-color="#000"
      color="#fff"
      loader="spinner"
    />
  </div>

  <div class="page-wrapper">
    <!-- ********    TOP-HEADER SECTION    ******** -->
    <TopHeader />
    <!-- ********    END TOP-HEADER SECTION    ******** -->

    <!-- ********    HEADER SECTION    ******** -->
    <div class="nav__toggle hidden"></div>
    <div class="header-section">
      <div class="top-content">
        <div></div>
        <div class="top-content-right">
          <h1 class="h1">
            {{ $t("makePayment.heroTitle1") }}
            <span class="highlight text-second">{{
              $t("makePayment.heroTitle2")
            }}</span>
          </h1>
          <p class="p">{{ $t("makePayment.heroDesc") }}</p>
        </div>
      </div>
      <div
        class="journey-date bg-white-opacity text-black text-sm flex flex-row justify-between rounded-lg"
      >
        <div class="flex flex-col items-start p-3">
          <div
            class="flex flex-row gap-2 text-white text-[10px] md:text-[16px]"
          >
            <div>{{ fromName }}</div>
            <i class="bi bi-arrow-right-short text-theme text-[20px]"></i>
            <div>{{ toName }}</div>
          </div>
          <div class="text-gray-400 text-[12px] md:text-[16px]">
            <div>
              {{
                formatDate(busDetails.trip?.trip_date || busDetails.trip_date)
              }}
            </div>
          </div>
        </div>
        <div
          class="date-button flex flex-row justify-center items-center cursor-pointer bg-theme text-[14px] md:text-[16px] rounded-r-lg gap-2 p-3"
          @click="goHome"
        >
          <i class="bi bi-house"></i>
          <button>{{ $t("makePayment.home") }}</button>
        </div>
      </div>
    </div>
    <!-- ********    END HEADER SECTION    ******** -->

    <!-- ********    BODY SECTION    ******** -->
    <section class="seat-layout-section grid grid-cols-12 gap-3">
      <div class="bus-info col-span-12 md:col-span-6 lg:col-span-4">
        <!-- ****** TICKET SUMMARY ****** -->
        <div
          class="ticket-info col-span-3 lg:col-span-1 p-3 rounded-lg shadow-md w-full min-h-auto"
        >
          <div class="flex flex-row justify-between items-center mb-2">
            <div class="font-bold text-sm">
              {{ $t("makePayment.ticketSummary") }}
            </div>
          </div>
          <div class="w-full h-[0.7px] bg-gray-300 mb-2"></div>
          <div class="grid grid-cols-7 gap-2 text-[12px] text-gray-500 mb-2">
            <div class="col-span-2">{{ $t("makePayment.fromLabel") }}</div>
            <div class="col-span-5">
              {{
                busDetails.route?.start_point?.name ||
                busDetails.trip_route?.start_point
              }}
            </div>
          </div>
          <div class="grid grid-cols-7 gap-2 text-[12px] text-gray-500 mb-2">
            <div class="col-span-2">{{ $t("makePayment.toLabel") }}</div>
            <div class="col-span-5">
              {{
                busDetails.route?.end_point?.name ||
                busDetails.trip_route?.end_point
              }}
            </div>
          </div>
          <div class="grid grid-cols-7 gap-2 text-[12px] text-gray-500 mb-2">
            <div class="col-span-2">{{ $t("makePayment.dateLabel") }}</div>
            <div class="col-span-5">
              {{
                formatDate(busDetails.trip?.trip_date || busDetails.trip_date)
              }}
            </div>
          </div>
          <div class="grid grid-cols-7 gap-2 text-[12px] text-gray-500 mb-2">
            <div class="col-span-2">{{ $t("makePayment.reportingLabel") }}</div>
            <div class="col-span-5">
              {{ twelveHourFormat(busDetails.reporting_time) }}
            </div>
          </div>
          <div class="grid grid-cols-7 gap-2 text-[12px] text-gray-500 mb-2">
            <div class="col-span-2">{{ $t("makePayment.departingLabel") }}</div>
            <div class="col-span-5">
              {{ twelveHourFormat(busDetails.departing_time) }}
            </div>
          </div>
          <div
            v-if="busDetails.arrival_time"
            class="grid grid-cols-7 gap-2 text-[12px] text-gray-500 mb-2"
          >
            <div class="col-span-2">{{ $t("makePayment.arrivalLabel") }}</div>
            <div class="col-span-5">
              {{ twelveHourFormat(busDetails.arrival_time) }}
            </div>
          </div>
          <div class="grid grid-cols-7 gap-2 text-[12px] text-gray-500">
            <div class="col-span-2">{{ $t("makePayment.fareLabel") }}</div>
            <div class="col-span-5">{{ busDetails.fare }}/=</div>
          </div>
          <div class="w-full h-[0.7px] bg-gray-300 my-2"></div>
          <div class="flex flex-row gap-4 text-[12px] text-gray-500">
            <div class="">{{ $t("makePayment.paymentLabel") }}</div>
            <div
              v-if="paymentStatus.success"
              class="flex bg-green-500 text-white text-[11px] px-3 py-[6px] rounded-2xl"
            >
              {{ paymentStatus.message }}
            </div>
            <div
              v-else
              class="bg-gray-400 text-white text-[11px] px-3 py-[6px] rounded-2xl"
            >
              {{ paymentStatus.message ?? $t("makePayment.pending") }}
            </div>
          </div>
        </div>
      </div>
      <div class="col-span-12 md:col-span-6 lg:col-span-8">
        <div
          class="seat-info grid-cols-3 col-span-3 lg:col-span-1 p-3 rounded-lg shadow-md w-full"
        >
          <h1
            class="h1 text-theme p-2 my-3 flex items-center justify-center border rounded-md"
          >
            {{ $t("makePayment.remaining") }} {{ formattedTime }} min
          </h1>
          <div class="font-bold text-sm">
            {{ $t("makePayment.paymentSummary") }}
          </div>
          <div class="text-sm text-gray-500 pb-2">
            {{ $t("makePayment.confirmInfo") }}
          </div>
          <div class="w-full h-[0.7px] bg-gray-300 mb-2"></div>
          <div class="text-sm">{{ $t("makePayment.paymentMethod") }}</div>
          <div class="text-[12px] text-gray-500 pb-2">
            {{ $t("makePayment.chooseMethod") }}
          </div>
          <div class="w-full h-[0.7px] bg-gray-300 mb-3"></div>
          <form class="user-details" @submit.prevent="makePayment">
            <div class="text-sm text-gray-500 pb-1 pt-4">
              {{ $t("makePayment.channelLabel") }}
            </div>

            <div
              class="channels-container"
              role="radiogroup"
              :aria-label="$t('makePayment.channelLabel')"
            >
              <button
                v-for="channel in paymentChannels"
                :key="channel.partnerName"
                :aria-checked="selectedPaymentChannel === channel.partnerName"
                :class="{
                  'channel-card--selected':
                    selectedPaymentChannel === channel.partnerName,
                }"
                class="channel-card"
                role="radio"
                type="button"
                @click="selectedPaymentChannel = channel.partnerName"
              >
                <!-- Selected badge -->
                <span
                  v-if="selectedPaymentChannel === channel.partnerName"
                  class="channel-card__check"
                >
                  <i class="fi fi-br-check"></i>
                </span>

                <!-- Logo, boxed to a consistent size so every provider
                     reads at the same visual weight regardless of its
                     source image's own dimensions/aspect ratio -->
                <span class="channel-card__logo">
                  <img
                    :alt="channel.partnerName + ' logo'"
                    :src="channel.logoUrl"
                    loading="lazy"
                  />
                </span>

                <span class="channel-card__name">{{ channel.partnerName }}</span>
              </button>
            </div>

            <div class="text-sm text-gray-500 pb-1">
              {{ $t("makePayment.phoneLabel") }}
            </div>
            <input
              id="phone-number"
              v-model="phone_number"
              class="p-2 mb-0"
              maxlength="10"
              minlength="10"
              name="phone-number"
              :placeholder="$t('makePayment.phonePlaceholder')"
              required
              type="tel"
            />
            <div class="text-[12px] text-gray-500 pb-8">
              {{ $t("makePayment.paymentNumberFor") }}
              {{ selectedPaymentChannel }}.
            </div>

            <div class="text-[12px] text-gray-500 pb-3">
              {{ $t("makePayment.afterClickPayNow") }}
              <span class="text-black font-bold"
                >"{{ $t("makePayment.payNowBtn") }}"</span
              >
              {{ $t("makePayment.receiveRequest") }}
              <span class="text-black font-bold">{{
                selectedPaymentChannel
              }}</span>
              {{ $t("makePayment.mobile") }}
            </div>

            <div class="text-[12px] text-gray-500 py-3">
              {{ $t("makePayment.totalFare") }}
              <span class="text-black font-bold"
                >TSH{{
                  bookings.reduce((total, booking) => total + booking.price, 0)
                }}/=</span
              >
            </div>

            <div class="w-full h-[0.7px] bg-gray-300 mb-3"></div>

            <div class="w-full flex justify-center">
              <button class="primary-button text-[12px]" type="submit">
                {{ $t("makePayment.payNow") }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
    <!-- ********    END BODY SECTION    ******** -->

    <!-- ********    FOOTER SECTION    ******** -->
    <section class="footer-section">
      <Footer />
    </section>
    <!-- ********    END FOOTER SECTION    ******** -->
  </div>
</template>

<script>
import Multiselect from "@vueform/multiselect";
import Footer from "../../components/Footer.vue";
import TopHeader from "../../components/TopHeader.vue";
import CustomSwitch from "../../components/CustomSwitch.vue";
import { useToast } from "vue-toastification";

import sessionManager from "../../../core/utils/sessionManager.js";
import PaymentService from "../../../core/services/paymentService.js";

import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/css/index.css";

export default {
  data() {
    return {
      isSwitched: true,
      fromName: "",
      toName: "",
      tripDate: "",
      journeyData: {},
      journeyId: 0,
      isLoading: false,
      phone_number: "",
      paymentChannels: [],
      bookings: [],
      selectedPaymentChannel: "",
      intervalId: null,
      paymentStatus: {},
      busDetails: { trip_route: {}, route: {}, trip: {} },
      timeLeft: 15 * 60,
      timer: null,
    };
  },
  components: {
    Loading,
    Footer,
    TopHeader,
    CustomSwitch,
    Multiselect,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  computed: {
    formattedTime() {
      const minutes = Math.floor(this.timeLeft / 60);
      const seconds = this.timeLeft % 60;
      return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    },
  },
  methods: {
    goHome() {
      window.location.href = "/";
    },

    async fetchData() {
      this.isLoading = true;
      const paymentService = new PaymentService();
      await paymentService.getPaymentGatewayChannels();
      this.paymentChannels = paymentService.paymentGatewayChannelList;
      this.isLoading = false;
    },
    async makePayment() {
      if (this.selectedPaymentChannel === "") {
        this.toast.info(this.$t("makePayment.selectChannelToast"));
        return;
      }
      const paymentService = new PaymentService();
      this.isLoading = true;
      const data = {
        phone_number: this.phone_number,
        booking_number: this.bookings.map((b) => b.booking_number),
        provider: this.selectedPaymentChannel,
      };
      await paymentService.makePayment(data);
      if (!paymentService.success.value) {
        this.toast.error(paymentService.message.value);
      } else {
        this.toast.success(paymentService.message.value);
        this.$router.push({
          path: "/ticket",
          hash: "#body-section",
        });
      }
      this.isLoading = false;
    },
    async checkPaymentStatus() {
      const paymentService = new PaymentService();
      await paymentService.checkPaymentStatus();
      this.paymentStatus = paymentService.paymentStatus.value;
    },

    startCountdown() {
      if (this.timer) return;

      this.timer = setInterval(() => {
        if (this.timeLeft > 0) {
          this.timeLeft--;
        } else {
          clearInterval(this.timer);
          this.timer = null;
          this.navigateToHome();
        }
      }, 1000);
    },
    navigateToHome() {
      window.location.href = "/"; // Redirect to home page
    },
  },
  mounted() {
    this.startCountdown();
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  created() {
    this.busDetails = sessionManager.getBusDetails() ?? {
      trip_route: {},
      route: {},
      trip: {},
    };
    this.fromName = sessionManager.fromName.value;
    this.toName = sessionManager.toName.value;
    this.tripDate = sessionManager.date.value;
    this.bookings = sessionManager.getBookingDetails() ?? [];
    this.timeLeft = sessionManager.getBookingExpiresIn();
    this.checkPaymentStatus();
    this.fetchData();
  },
};
</script>

<style scoped>
.bg-white-opacity {
  background-color: rgba(0, 0, 0, 0.5);
}

/* ****** PAYMENT CHANNELS ****** */
.channels-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(130px, 1fr));
  gap: 12px;
  margin: 4px 0 16px;
}

.channel-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 10px;
  background-color: #fff;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  cursor: pointer;
  font: inherit;
  text-align: center;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out,
    background-color 0.2s ease-in-out, transform 0.2s ease-in-out;
}

.channel-card:hover {
  border-color: rgba(216, 3, 34, 0.6);
  transform: translateY(-2px);
}

.channel-card:focus-visible {
  outline: 2px solid #d80322;
  outline-offset: 2px;
}

.channel-card--selected {
  border-color: #d80322;
  background-color: rgba(216, 3, 34, 0.06);
  box-shadow: 0 4px 14px rgba(34, 178, 71, 0.35);
}

.channel-card__logo {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 42px;
  padding: 4px 8px;
  background-color: #f7f7f8;
  border-radius: 8px;
  overflow: hidden;
}

.channel-card__logo img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.channel-card__name {
  width: 100%;
  overflow: hidden;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.channel-card__check {
  position: absolute;
  top: -8px;
  right: -8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 5px 4px 2px 4px;
  color: #fff;
  font-size: 10px;
  background-color: #22b247;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

@media (max-width: 480px) {
  .channels-container {
    grid-template-columns: repeat(2, 1fr);
  }
}

.page-wrapper {
  overflow-x: hidden;
}

.header-section {
  position: relative;
  text-align: center;
  padding: 20px;
  height: 360px;
  background-image: url("@/assets/images/mumuk-hero.jpeg");
  background-size: cover;
  background-position: center;
  color: white;
  overflow: hidden;
}

.header-section::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6); /* Black with 50% opacity */
  z-index: 1; /* Position the overlay behind the content */
}

.header-section * {
  position: relative;
  z-index: 2; /* Ensure content is above the overlay */
}

.journey-date {
  margin-left: 260px;
  margin-right: 260px;
}

.seat-layout-section {
  margin: 60px 160px;
}

.seats-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.available-bus-item {
  width: 100%;
  position: relative;
  background-color: white;
  border: #e2dedc7e 0.1px solid;
  border-radius: 16px;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2);
}

.bus-name {
  font-size: 10px;
  position: absolute;
  top: 20px;
  left: 20px;
  background-color: rgba(0, 0, 0, 0.611);
  color: white;
  font-weight: 500;
}

.available-bus-item img {
  height: 200px;
  width: 100%;
  /* height: 100%; */
  object-fit: cover;
  border-radius: 16px 16px 0 0;
}

.available-bus-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 6px 20px;
}

@media (max-width: 1184px) {
  .seat-layout-section {
    margin: 60px 80px;
  }

  .journey-date {
    margin-left: 100px;
    margin-right: 100px;
  }
}

@media (max-width: 820px) {
  .seat-layout-section {
    margin: 40px 60px;
  }
}

@media (max-width: 768px) {
  .journey-date {
    margin-left: 0px;
    margin-right: 0px;
  }

  .seat-layout-section {
    margin: 20px 20px;
  }

  .header-section {
    height: 500px !important;
  }
}

@media (max-width: 576px) {
  .header-section {
    height: 500px !important;
  }
}
</style>
